import type Quill from 'quill';

// The paragraphs the guard adds are an editing affordance, not content: they
// exist so the caret has somewhere to stand next to a block embed. Serializing
// them would persist blank lines the user never typed, and the hosts that strip
// the signature marker on save (EmailTemplateCardForm, EmailForm) would leave
// them behind, growing the stored body by one paragraph per edit.
// Derived from the same blot names the guard uses, so the two cannot drift: a
// block embed writes its own name as `data-<blotName>` (see quillBlots).
// The exact inverse of `missingGuardIndexes`: a paragraph is the guard's when an
// embed sits on one side and the document edge or another embed on the other.
// An empty paragraph with no embed beside it is the user's and stays.
const guardParagraphs = (
  blotNames: ReadonlySet<string>,
): [RegExp, string][] => {
  const embed = `<div (?:${[...blotNames]
    .map(name => `data-${name}`)
    .join('|')})="[^"]*"></div>`;
  return [
    [new RegExp(`(${embed})<p><br/?></p>(?=${embed}|$)`, 'g'), '$1'],
    [new RegExp(`^<p><br/?></p>(?=${embed})`), ''],
  ];
};

export const stripGuardParagraphs = (
  html: string,
  blotNames: ReadonlySet<string>,
): string =>
  guardParagraphs(blotNames).reduce(
    (out, [pattern, replacement]) => out.replace(pattern, replacement),
    html,
  );

export const canonicalHtmlFromQuill = (
  quill: Quill,
  lineBlockEmbeds: ReadonlySet<string>,
): string =>
  stripGuardParagraphs(
    quill
      .getSemanticHTML()
      // getSemanticHTML drops the placeholder <br> of empty lines, leaving
      // zero-height <p></p> that collapse when the email is rendered.
      .replace(/<p><\/p>/g, '<p><br/></p>')
      .replace(/\u00A0/g, ' '),
    lineBlockEmbeds,
  );

export const collapseSingleLineBreaks = (editor: Quill): boolean => {
  const text = editor.getText();
  let changed = false;

  // Quill keeps one final newline for every document. Keep that terminator, but
  // turn every user-entered or pasted line break into a space.
  for (let index = text.length - 2; index >= 0; index -= 1) {
    if (text[index] === '\n') {
      editor.deleteText(index, 1, 'silent');
      editor.insertText(index, ' ', 'silent');
      changed = true;
    }
  }

  return changed;
};

type EmbedOp = { insert?: unknown };

const isLineBlockEmbed = (
  op: EmbedOp | undefined,
  blotNames: ReadonlySet<string>,
): boolean => {
  const insert = op?.insert;
  return (
    insert != null &&
    typeof insert === 'object' &&
    blotNames.has(Object.keys(insert)[0] ?? '')
  );
};

// A block embed owns its whole line, and Quill exposes no caret position where
// one meets the start of the document, its end, or another block embed. Typing
// into such a gap does not fail loudly - the text lands elsewhere in the
// document - so every gap gets an empty paragraph. Returns the document indexes
// that need one, in order.
export const missingGuardIndexes = (
  ops: readonly EmbedOp[],
  blotNames: ReadonlySet<string>,
): number[] => {
  const gaps: number[] = [];
  let index = 0;
  let previousWasEmbed = false;
  for (const op of ops) {
    const embed = isLineBlockEmbed(op, blotNames);
    if (embed && (index === 0 || previousWasEmbed)) {
      gaps.push(index);
    }
    index += typeof op.insert === 'string' ? op.insert.length : 1;
    previousWasEmbed = embed;
  }
  if (previousWasEmbed) {
    gaps.push(index);
  }
  return gaps;
};
